import { v1 as uuidv1 } from 'uuid';
import type { RaceEventConfig, RaceEventState } from './types';
import { raceEventConfigs } from './raceEventConfigs';
import { MS_IN_HOUR } from '../../utils/time-utils';
import { generateRaceEventOpponents, getRaceLeaderboard, updateOpponents } from './raceOpponents';
import { EventConditionType } from '../../app/state/events/types';
import type { RaceEventDependencies, RaceEventObservable } from './RaceEventController';

function getRaceEventToStart(dependencies: RaceEventDependencies): RaceEventConfig | null {
  const { observable, isConditionMet } = dependencies;

  if (observable.getValue().raceEvent !== null) return null;
  return raceEventConfigs.find((config) => config.conditions.every((condition) => isConditionMet(condition))) ?? null;
}

export function getRaceEventMinLevel(): number {
  const minLevel = raceEventConfigs.reduce((acc, config) => {
    const levelReachCondition = config.conditions.find(
      (condition) => condition.type === EventConditionType.ReachedLevel,
    );
    if (levelReachCondition) {
      return Math.min(acc, levelReachCondition.level);
    }
    return acc;
  }, Infinity);

  return minLevel === Infinity ? 0 : minLevel;
}

function createRaceEventState(config: RaceEventConfig): RaceEventState {
  return {
    id: `race_${config.eventTitle}_${uuidv1()}`,
    config,
    joinedShowed: Date.now(),
    levelStart: 0,
    finished: false,
    currentProgress: 0,
    opponents: generateRaceEventOpponents(config),
    startDate: null,
    lastSeenLeaderboard: {},
  };
}

export function tryToActivateRaceEvent(dependencies: RaceEventDependencies): boolean {
  const config = getRaceEventToStart(dependencies);
  if (!config) return false;

  dependencies.observable.updateValue({
    raceEvent: createRaceEventState(config),
  });

  return true;
}

export function isRaceEventJoinExpired(raceState: RaceEventState): boolean {
  return (
    raceState.startDate === null && Date.now() - raceState.joinedShowed > raceState.config.joinWindowHours * MS_IN_HOUR
  );
}

export function updateRaceEvent(dependencies: RaceEventDependencies): void {
  const { observable } = dependencies;
  let { raceEvent } = observable.getValue();

  if (!raceEvent) return;
  if (!raceEvent.startDate) return;
  if (raceEvent.finished) return;

  const runOutOfTime = Date.now() - (raceEvent.startDate ?? 0) > raceEvent.config.durationHours * MS_IN_HOUR;
  const playerReachedTarget = raceEvent.currentProgress >= raceEvent.config.levelsToBeat;

  updateOpponents(observable);
  raceEvent = observable.getValue().raceEvent;

  if (raceEvent && (runOutOfTime || playerReachedTarget)) {
    observable.updateValue({
      raceEvent: {
        ...raceEvent,
        finished: true,
      },
    });
  }
}

export function joinRaceEvent(observable: RaceEventObservable): void {
  const raceState = observable.getValue().raceEvent;
  if (!raceState || raceState.startDate !== null || raceState.finished) return;

  if (isRaceEventJoinExpired(raceState)) {
    clearEndRaceEvent(observable);
    return;
  }

  observable.updateValue({
    raceEvent: {
      ...raceState,
      startDate: Date.now(),
      levelStart: observable.getValue().levelIndex + 1,
    },
  });
}

export function updateLastSeenRaceLeaderboard(observable: RaceEventObservable): void {
  const raceState = observable.getValue().raceEvent;
  if (!raceState) return;

  const lastSeenLeaderboard: RaceEventState['lastSeenLeaderboard'] = {};

  getRaceLeaderboard(raceState).forEach((entry) => {
    lastSeenLeaderboard[entry.player.currentUser ? 'current' : entry.player.id] = {
      score: entry.score,
      rank: entry.rank,
    };
  });

  observable.updateValue({
    raceEvent: {
      ...raceState,
      lastSeenLeaderboard,
    },
  });
}

export function clearEndRaceEvent(observable: RaceEventObservable): void {
  observable.updateValue({
    raceEvent: null,
    raceEventLastEndTime: Date.now(),
  });
}

export function getRaceEventEndTime(state: RaceEventState): number {
  if (!state.startDate) return 0;
  return state.startDate + state.config.durationHours * MS_IN_HOUR;
}

export function getPlayerRank(state: RaceEventState): number {
  const playerEntry = getRaceLeaderboard(state, { currentUser: true, id: '', name: '', avatarUrl: '' }).find(
    (entry) => entry.player.currentUser,
  );

  if (!playerEntry) return 0;
  return playerEntry.rank;
}

export function applyRaceEventReward(dependencies: RaceEventDependencies): void {
  const { observable, addResources } = dependencies;

  const state = observable.getValue().raceEvent;
  if (!state) return;

  const playerRank = getPlayerRank(state);

  const reward = state.config.rewards[playerRank - 1];
  if (!reward) return;

  addResources(reward, state.id ?? '');
}

export function raceEventWinLevel(observable: RaceEventObservable): void {
  const raceState = observable.getValue().raceEvent;
  if (!raceState) return;
  if (raceState.finished) return;
  if (raceState.startDate === null) return;

  if (Date.now() > getRaceEventEndTime(raceState)) {
    observable.updateValue({
      raceEvent: {
        ...raceState,
        finished: true,
      },
    });
    return;
  }

  const newProgress = Math.min(raceState.currentProgress + 1, raceState.config.levelsToBeat);
  const finished = newProgress >= raceState.config.levelsToBeat;

  observable.updateValue({
    raceEvent: {
      ...raceState,
      finished,
      currentProgress: Math.min(raceState.currentProgress + 1, raceState.config.levelsToBeat),
    },
  });
}
